import { gql } from '@apollo/client';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMatch } from '@tanstack/react-location';
import { memo } from 'react';
import PageContainer from '../../core/components/PageContainer';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import assert from '../../core/util/assert';
import PropertySelectionDialog from '../../property/components/PropertySelectionDialog';
import CheckinChecklist from '../../shared-ui/component/CheckinChecklist';
import CheckoutChecklist from '../../shared-ui/component/CheckoutChecklist';
import InstructionDownloadHistory from './InstructionDownloadHistory';
import { calculateInvoiceItems } from '../calculation/invoiceBreakdown';
import BookingBooleanField from './BookingBooleanField';
import BookingSelectField from './BookingSelectField';
import BookingTextField from './BookingTextField';
import CountryForm from './CountryForm';
import GuestList from './GuestList';

import useProperty from '../../property/hooks/useProperty';
import MarkAsCompleteButton from './MarkAsCompleteButton';

interface Guest {
    id: number;
    firstName: string;
    lastName: string;
    sex: string;
    nationality: string;
    country: string;
    birthDate: string;
    birthPlace: string;
    postcode: string;
    address: string;
    idDocumentType?: string;
    idDocumentId?: string;
    idDocumentIsPdf: boolean;
    valid: boolean;
    price: number;
    touristTax: number;
    nationalityNameHU: string;
    countryNameHU: string;
}

const bookingViewQuery = gql`
    query booking($id: Int!) {
        booking(id: $id) {
            id
            reference
            status
            invoiceId
            detailsRecorded
            actionRequired
            urgentActionRequired
            bookingShouldBeComplete
            countryCode
            country {
                nameHU
                nationalityHU
            }
            checkin
            checkinTime
            checkout
            checkoutTime
            revenue
            cleaningFee
            guestName
            lockboxCode
            checkinUrl
            guestCheckinUrl
            contactPhone
            guests {
                id
                firstName
                lastName
                sex
                nationality
                nationalityNameHU
                country
                countryNameHU
                birthDate
                birthPlace
                postcode
                address
                idDocumentType
                idDocumentId
                idDocumentIsPdf
                valid
                price
                touristTax
            }
            instructionDownloadCount
            lastInstructionDownload
            instructionDownloads {
                downloads {
                    timestamp
                    ipAddress
                    userAgent
                    type
                }
            }
        }
    }
`;

const BookingView = memo(() => {
    const { isModuleEnabled, isAirBnBEnabled } = useProperty();
    const propertyId = usePropertyId();
    const shouldSplitScreen = useMediaQuery('(min-width:820px)');
    const match = useMatch();

    const bookingId = +match.params.id;
    const pathname = window.location.hash.replace('#', '');

    // Only show property selection if we're on a legacy route (/booking/:id)
    const isLegacyBookingRoute = /^\/booking\/\d+/.test(pathname);

    // Show property selection if no property ID in URL and we're on a legacy route
    if (!propertyId && isLegacyBookingRoute) {
        return (
            <PropertySelectionDialog
                open={true}
                targetPath={`/booking/${bookingId}`}
            />
        );
    }

    const client = useApolloClient();
    const [addGuest, { loading: addGuestLoading }] = useMutation(
        gql`
            mutation addGuest($bookingId: Int!) {
                addGuest(bookingId: $bookingId)
            }
        `,
        {
            variables: {
                bookingId,
            },
        }
    );
    const [deleteInstructions] = useMutation(
        gql`
            mutation deleteInstructions($bookingId: Int!) {
                deleteInstructions(bookingId: $bookingId)
            }
        `,
        {
            refetchQueries: [
                {
                    query: bookingViewQuery,
                    variables: { bookingId },
                },
            ],
        }
    );

    const { data, loading, error } = useQuery<{
        booking: {
            id: number;
            reference: string;
            status: string;
            invoiceId?: string;
            detailsRecorded: boolean;
            countryCode: string;
            country: {
                nameHU: string;
                nationalityHU: string;
            };
            checkin: string;
            checkinTime: string;
            checkout: string;
            checkoutTime: string;
            revenue: number;
            cleaningFee: number;
            guestName: string;
            lockboxCode?: string;
            guestCheckinUrl: string;
            checkinUrl: string;
            actionRequired: boolean;
            urgentActionRequired: boolean;
            bookingShouldBeComplete: boolean;
            contactPhone: string;
            guests: Guest[];
            instructionDownloadCount: number;
            lastInstructionDownload?: string;
            instructionDownloads: {
                downloads: Array<{
                    timestamp: string;
                    ipAddress: string;
                    userAgent: string;
                    type: 'view' | 'pdf' | 'email';
                }>;
            };
        };
    }>(bookingViewQuery, {
        variables: { id: bookingId },
        skip: !match.params.id,
    });

    if (loading || addGuestLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: shouldSplitScreen ? 'row' : 'column-reverse',
                    gap: 3,
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {new Array(12).fill(0).map((_, index) => (
                        <Box key={index} /* NOSONAR */>
                            <Skeleton width={216} />
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    if (error) {
        return <>{(error as Error).toString()}</>;
    }

    assert(data);

    const { booking } = data;

    const invoiceItems = calculateInvoiceItems(booking);

    return (
        <PageContainer>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: shouldSplitScreen ? 'row' : 'column-reverse',
                    gap: 3,
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box>
                        <TextField
                            label="Reference"
                            value={booking.reference}
                        />
                    </Box>
                    <BookingSelectField
                        label="Status"
                        id={booking.id}
                        field="status"
                        value={booking.status}
                        options={[
                            'booked',
                            'accommodationVerified',
                            'keysSecured',
                            'hasReview',
                            'complete',
                            'canceled',
                            'Confirmed',
                        ]}
                    />
                    <Box>
                        {isAirBnBEnabled() && (
                            <Button
                                onClick={() => {
                                    window.open(
                                        `https://www.airbnb.co.uk/hosting/reservations/details/${booking.reference}`
                                    );
                                }}
                            >
                                Open booking
                            </Button>
                        )}
                        {isModuleEnabled('data-reporting/hungary') && (
                            <>
                                <Button
                                    onClick={() => {
                                        window.open(`${booking.checkinUrl}`);
                                    }}
                                >
                                    Open checkin
                                </Button>
                                <Button
                                    onClick={() => {
                                        window.open(
                                            `${booking.guestCheckinUrl}`
                                        );
                                    }}
                                >
                                    Open checkin (Checkin Portal)
                                </Button>
                            </>
                        )}
                    </Box>
                    <CountryForm
                        contactPhone={booking.contactPhone}
                        bookingId={booking.id}
                        value={booking.countryCode}
                    />
                    <BookingTextField
                        label="Lockbox code"
                        id={booking.id}
                        field="lockboxCode"
                        value={booking.lockboxCode}
                    />
                    <BookingTextField
                        label="Checkin date"
                        id={booking.id}
                        field="checkin"
                        value={booking.checkin}
                    />
                    <BookingTextField
                        label="Checkin time"
                        id={booking.id}
                        field="checkinTime"
                        value={booking.checkinTime}
                    />
                    <BookingTextField
                        label="Checkout date"
                        id={booking.id}
                        field="checkout"
                        value={booking.checkout}
                    />
                    <BookingTextField
                        label="Checkout time"
                        id={booking.id}
                        field="checkoutTime"
                        value={booking.checkoutTime}
                    />
                    <BookingTextField
                        label="Revenue"
                        id={booking.id}
                        field="revenue"
                        value={booking.revenue.toString()}
                        type="numeric"
                    />
                    <BookingTextField
                        label="Invoice id"
                        id={booking.id}
                        field="invoiceId"
                        value={booking.invoiceId}
                    />
                    <Box>
                        <h3>Invoice breakdown</h3>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Accommodation Price"
                                value={invoiceItems.accommodationPrice}
                            />
                            <TextField
                                label="Cleaning fee"
                                value={invoiceItems.cleaningFee}
                            />
                            <TextField
                                label="Tourist tax"
                                value={invoiceItems.touristTax}
                            />
                            <TextField
                                label="Admin fee"
                                value={invoiceItems.adminFee}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {isModuleEnabled('data-reporting/hungary') && (
                            <Button
                                variant="contained"
                                onClick={() => {
                                    addGuest()
                                        .then(async () => {
                                            await client.clearStore();
                                            await client.refetchObservableQueries();
                                        })
                                        .catch(addError => {
                                            throw addError;
                                        });
                                }}
                            >
                                Add guest
                            </Button>
                        )}

                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => {
                                deleteInstructions({
                                    variables: {
                                        bookingId: booking.id,
                                    },
                                })
                                    .then(async () => {
                                        await client.clearStore();
                                        await client.refetchObservableQueries();
                                    })
                                    .catch(addError => {
                                        throw addError;
                                    });
                            }}
                        >
                            Delete instructions
                        </Button>
                    </Box>

                    <BookingBooleanField
                        label={'Details recorded'}
                        field={'detailsRecorded'}
                        value={booking.detailsRecorded}
                        id={booking.id}
                    />

                    {isModuleEnabled('data-reporting/hungary') && (
                        <GuestList
                            guests={booking.guests}
                            bookingId={booking.id}
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <CheckinChecklist bookingId={booking.id} />
                    <CheckoutChecklist bookingId={booking.id} />
                    <InstructionDownloadHistory
                        downloads={booking.instructionDownloads.downloads}
                        downloadCount={booking.instructionDownloadCount}
                        lastDownload={booking.lastInstructionDownload}
                    />
                    {booking.bookingShouldBeComplete && (
                        <MarkAsCompleteButton booking={booking} />
                    )}
                </Box>
            </Box>
        </PageContainer>
    );
});

export default BookingView;
